import React, { ChangeEvent, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { makeStyles, Typography } from 'transfix-ui/core'
import { autoConfirmStop } from 'utils/actions/shipmentStop'
import { updateAppointmentReference } from 'apps/schedule/utils/actions/confirmReference'
import { IStop } from 'apps/schedule/utils/types/stops'
import { brandColors, Tag, TAG_SIZE, TAG_STYLE, TextField } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { DISPLAY } from 'constants/display'
import {
  alreadyConfirmedText,
  appointmentDate,
  unconfirmedAppointmentDate,
} from '../../utils/schedule'
import { FCFSButton } from './Buttons/FCFSButton'
import { TimeButtonList } from './Buttons/TimeButtonList'

export interface IStopCard {
  apptType: NonNullable<IStop['appointmentType']>
  autoConfirmToken: string
  confirmSuccess: boolean
  mainStop: IStop
  stopType: NonNullable<IStop['stopType']>
  isMainStop?: boolean
}

const useStopCardStyles = makeStyles(theme => ({
  card: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(3),
  },
  info: {
    padding: theme.spacing(3, 0),
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: theme.spacing(1.5),
  },
  confirmationInputContainer: {
    padding: theme.spacing(3, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    borderTop: `1px solid ${brandColors.coolGray3}`,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'flex-end',
    },
  },
  buttonListContainer: {
    padding: theme.spacing(3, 0),
    borderTop: `1px solid ${brandColors.coolGray3}`,
  },
  messageContainer: {
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${brandColors.coolGray3}`,
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row-reverse',
      justifyContent: 'space-between',
    },
  },
}))

export const StopCard = React.memo(
  ({ apptType, autoConfirmToken, confirmSuccess, isMainStop, mainStop, stopType }: IStopCard) => {
    const styles = useStopCardStyles()
    // Alright so this is confusing, but coupled enough that it's easier to just deal with it;
    // alreadyConfirmed (defined below, calculated by checking apopintmentStatus) is whether or not the appt was confirmed already;
    // confirmed is whether or not THIS APP SUCCESSFULLY CONFIRMED IT.
    const {
      appointmentReference,
      appointmentStatus,
      bol,
      city,
      confirmationReference,
      confirmed,
      endAtTime,
      facilityName,
      isDeprecated,
      isDropTrailer,
      po,
      shipperName,
      startAtTime,
      stateCode,
      stopId,
      vendorName,
    } = mainStop || {}

    const dispatch = useDispatch()

    const updateReference = (id: string | number, confirmationReference_: string) =>
      dispatch(updateAppointmentReference(id, confirmationReference_, autoConfirmToken))

    const alreadyConfirmed = appointmentStatus === 'confirmed'
    const confirmedMessage = useMemo(
      () => (alreadyConfirmed ? alreadyConfirmedText(mainStop, stopType) : null),
      [alreadyConfirmed, mainStop, stopType]
    )
    const appointmentDateMemoized = useMemo(
      () =>
        confirmed || alreadyConfirmed
          ? appointmentDate(mainStop, apptType)
          : unconfirmedAppointmentDate(mainStop),
      [mainStop, apptType, confirmed, alreadyConfirmed]
    )

    const [apptRef, setApptRef] = useState('')
    const [validationMsg, setValidationMsg] = useState('')

    const changeApptRefHandler = (e: ChangeEvent<HTMLInputElement>) => {
      setApptRef(e.target.value)
    }

    const submitApptRefHandler = () => {
      if (apptRef && stopId) {
        updateReference(stopId, apptRef)
      } else {
        setValidationMsg('Please enter an appointment reference')
      }
    }

    const expiredPath = isDeprecated && !alreadyConfirmed && !confirmed
    const confirmNewDateTime = () =>
      dispatch(autoConfirmStop(stopId, autoConfirmToken, startAtTime, endAtTime))

    return (
      <div>
        {(confirmed || alreadyConfirmed) && (
          <Typography variant='h3'>{confirmedMessage}</Typography>
        )}
        <Card className={styles.card}>
          <div className={styles.title}>
            <div>
              {confirmed || alreadyConfirmed ? (
                <Tag label='Confirmed' size={TAG_SIZE.medium} tagStyle={TAG_STYLE.success} />
              ) : (
                <Tag label='Unconfirmed' size={TAG_SIZE.medium} tagStyle={TAG_STYLE.dark} />
              )}
            </div>
            {isMainStop ? (
              <Typography variant='h2'>
                {capitalize(stopType || mainStop?.stopType)} on {appointmentDateMemoized}
              </Typography>
            ) : (
              <Typography variant='h3'>
                {capitalize(stopType || mainStop?.stopType)} {appointmentDateMemoized}
              </Typography>
            )}
          </div>
          <div className={styles.info}>
            <Typography variant='body1' color='textSecondary'>
              Shipper
            </Typography>
            <Typography variant='body1'>{shipperName || DISPLAY.empty}</Typography>
            <Typography variant='body1' color='textSecondary'>
              Facility
            </Typography>
            <Typography variant='body1'>{vendorName || facilityName || DISPLAY.empty}</Typography>
            <Typography variant='body1' color='textSecondary'>
              Location
            </Typography>
            <Typography variant='body1'>
              {city}, {stateCode}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              Live/Drop
            </Typography>
            <Typography variant='body1'>
              {isDropTrailer ? 'Drop Appointment' : 'Live Appointment'}
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              PO
            </Typography>
            <Typography variant='body1'>{po || DISPLAY.empty}</Typography>
            <Typography variant='body1' color='textSecondary'>
              BOL
            </Typography>
            <Typography variant='body1'>{bol || DISPLAY.empty}</Typography>
            <Typography variant='body1' color='textSecondary'>
              Appt #
            </Typography>
            <Typography variant='body1'>{appointmentReference || DISPLAY.empty}</Typography>
            {confirmationReference && (
              <>
                <Typography variant='body1' color='textSecondary'>
                  Confirmation #
                </Typography>
                <Typography variant='body1'>{confirmationReference || DISPLAY.empty}</Typography>
              </>
            )}
          </div>
          {(confirmed || alreadyConfirmed) && !isDeprecated && (
            <>
              {!confirmSuccess && !confirmationReference && (
                <div className={styles.confirmationInputContainer}>
                  <TextField
                    dataTest='appt-ref-input'
                    label='Confirmation # (Optional)'
                    clearable={false}
                    placeholder='Enter confirmation number'
                    onChange={changeApptRefHandler}
                    value={apptRef}
                    error={Boolean(validationMsg)}
                    validationMessage={validationMsg}
                  />
                  <div>
                    <Button
                      data-test='appt-conf-btn'
                      color='primary'
                      onClick={submitApptRefHandler}
                      size='large'>
                      Submit
                    </Button>
                  </div>
                </div>
              )}
              {confirmSuccess && (
                <div className={styles.messageContainer}>
                  <InlineMessage
                    type={INLINE_MESSAGE_VARIANTS.INFO}
                    message='Confirmation number saved!'
                    dataTest='appt-conf-success'
                  />
                </div>
              )}
            </>
          )}
          {!(confirmed || alreadyConfirmed) && (
            <div className={styles.buttonListContainer}>
              {(apptType === 'appointment' || !apptType) && (
                <TimeButtonList stopType={stopType} stop={mainStop} />
              )}
              {(apptType === 'fcfs' || apptType === 'range') && <FCFSButton stop={mainStop} />}
            </div>
          )}
          {expiredPath && (
            <div className={styles.messageContainer}>
              <Button color='primary' onClick={confirmNewDateTime} size='large'>
                Confirm Appointment on {appointmentDateMemoized}
              </Button>
            </div>
          )}
        </Card>
      </div>
    )
  }
)
